import { Undo2, RotateCcw, Layers, Volume2, VolumeX } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export function ControlsBar({
  deckCount,
  historyLength,
  showCount,
  setShowCount,
  soundOn,
  setSoundOn,
  onDeckChange,
  onUndo,
  onReset,
}) {
  return (
    <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="controls-bar">
      <div className="flex items-center gap-2">
        <Layers className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
        <Select value={String(deckCount)} onValueChange={onDeckChange}>
          <SelectTrigger
            className="w-[130px] border-2 border-foreground rounded-none font-mono font-bold bg-card"
            data-testid="deck-select"
            aria-label="Number of decks"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="border-2 border-foreground rounded-none bg-popover">
            {[1, 2, 3, 4].map((n) => (
              <SelectItem key={n} value={String(n)} data-testid={`deck-option-${n}`}>
                {n} Deck{n > 1 ? "s" : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Button
        type="button"
        variant="outline"
        data-testid="undo-btn"
        onClick={onUndo}
        disabled={historyLength === 0}
        className="border-2 border-foreground rounded-none font-mono font-bold bg-card hover:bg-accent gap-2"
      >
        <Undo2 className="w-4 h-4" />
        Undo
        {historyLength > 0 && (
          <span className="text-xs text-muted-foreground">({historyLength})</span>
        )}
      </Button>

      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            type="button"
            variant="outline"
            data-testid="reset-btn"
            className="border-2 border-foreground rounded-none font-mono font-bold bg-card hover:bg-destructive hover:text-destructive-foreground gap-2"
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent className="border-2 border-foreground rounded-none bg-popover">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-cabinet tracking-tight">Reset the shoe?</AlertDialogTitle>
            <AlertDialogDescription className="font-mono">
              This clears all dealt cards and undo history for the current {deckCount}-deck shoe. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reset-cancel" className="border-2 border-foreground rounded-none font-mono">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="reset-confirm"
              onClick={onReset}
              className="border-2 border-foreground rounded-none font-mono bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Reset shoe
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <button
        type="button"
        data-testid="sound-toggle"
        aria-label={soundOn ? "Mute deal sound" : "Unmute deal sound"}
        aria-pressed={soundOn}
        onClick={() => setSoundOn((v) => !v)}
        className="ml-auto w-10 h-10 border-2 border-foreground bg-card flex items-center justify-center transition-[transform,box-shadow] duration-75 hover:-translate-y-0.5 hover:shadow-brutal-sm active:translate-y-0 active:shadow-none"
      >
        {soundOn ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5 text-muted-foreground" />}
      </button>

      <div className="flex items-center gap-2 border-2 border-foreground bg-card px-3 py-2">
        <Switch
          id="count-toggle"
          data-testid="count-toggle"
          checked={showCount}
          onCheckedChange={setShowCount}
        />
        <label htmlFor="count-toggle" className="text-xs uppercase tracking-[0.15em] cursor-pointer select-none">
          Hi-Lo Count
        </label>
      </div>
    </div>
  );
}
